'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';

import Footer from '@/components/Footer';
import Navigation from '@/components/Navigation';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Brand {
  name: string;
  tagline: string;
  href: string;
}

interface Review {
  quote: string;
  author: string;
  context: string;
  stars: number;
}

interface ProcessStep {
  number: string;
  title: string;
  description: string;
}

interface FAQ {
  question: string;
  answer: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const brands: Brand[] = [
  { name: 'Uno de 50', tagline: 'Bold Spanish silver, handcrafted in Madrid', href: '/brands#uno-de-50' },
  { name: 'ChloBo', tagline: 'Delicate layering pieces with spiritual meaning', href: '/brands#chlobo' },
  { name: 'Anna Beck', tagline: 'Artisan-crafted sterling silver from Bali', href: '/brands#anna-beck' },
  { name: 'Tilly Sveaas', tagline: 'Minimal Scandinavian-inspired fine jewellery', href: '/brands#tilly-sveaas' },
  { name: 'Muru', tagline: 'Meaningful symbols, ethically sourced silver', href: '/brands#muru' },
  { name: 'SVP Rings', tagline: 'Sculptural statement rings in sterling silver', href: '/brands#svp-rings' },
  { name: '+ More', tagline: 'Discover our ever-growing collection in-store', href: '/brands' },
];

const reviews: Review[] = [
  {
    quote: '"Amanda\'s boutique is an absolute gem. The curation is impeccable — every piece feels considered and special. I always leave with something I treasure."',
    author: 'Sarah M.',
    context: 'Cowbridge — Google Review ★★★★★',
    stars: 5,
  },
  {
    quote: '"Discovered this shop while visiting Cowbridge and it\'s now my go-to for gifts. The Uno de 50 range is stunning and Amanda\'s knowledge of each brand is second to none."',
    author: 'Rachel T.',
    context: 'Cardiff — Google Review ★★★★★',
    stars: 5,
  },
  {
    quote: '"Beautifully presented boutique with a genuinely personal service. I came in for a birthday gift and left with the perfect piece — and a lot of inspiration for my own wishlist."',
    author: 'Claire W.',
    context: 'Vale of Glamorgan — Google Review ★★★★★',
    stars: 5,
  },
];

const processSteps: ProcessStep[] = [
  {
    number: '01',
    title: 'Find Us',
    description: 'We\'re on Cowbridge High Street, South Wales. Free parking nearby and easy to reach from Cardiff, Barry, and the Vale of Glamorgan.',
  },
  {
    number: '02',
    title: 'Browse In-Store',
    description: 'The full range of designer brands is only available to view in person. Come and discover pieces you won\'t find anywhere else in Wales.',
  },
  {
    number: '03',
    title: 'Personal Styling',
    description: 'Amanda and the team are on hand to help you pair pieces, find the perfect gift, or build a layered look that\'s uniquely yours.',
  },
  {
    number: '04',
    title: 'Aftercare',
    description: 'We offer guidance on cleaning, sizing, and care for all stocked brands. We want your jewellery to last a lifetime.',
  },
];

const faqs: FAQ[] = [
  {
    question: 'What brands do you stock?',
    answer: 'We currently stock Uno de 50, ChloBo, Anna Beck, Tilly Sveaas, Muru, SVP Rings, and a selection of other carefully chosen designer silver brands. Visit us in-store or check our Brands page for the full current range.',
  },
  {
    question: 'Do you sell online or in-store only?',
    answer: 'Amanda Jones Jewellery is an in-store boutique experience. We believe jewellery should be seen, felt, and tried on before you buy. Visit us in Cowbridge to explore the full collection.',
  },
  {
    question: 'Do you offer gift wrapping?',
    answer: 'Yes — all purchases come beautifully presented. Please ask in-store about our gift wrapping options for special occasions.',
  },
  {
    question: 'Can you resize rings or repair jewellery?',
    answer: 'We can advise on sizing and care for all brands we stock, and can point you in the right direction for repairs. Please contact us or pop in to discuss your specific piece.',
  },
  {
    question: 'Where are you based and is there parking nearby?',
    answer: 'We\'re on Cowbridge High Street in the Vale of Glamorgan, South Wales. There is public parking available nearby on the High Street and in the town car parks.',
  },
];

// ─── Category thumbnails data ─────────────────────────────────────────────────
const categories = [
  {
    label: 'Rings',
    src: '/assets/images/image-1788603702598.png',
    alt: 'Stacked silver gemstone rings close-up',
    href: '/brands',
  },
  {
    label: 'Bracelets',
    src: '/assets/images/image-1788603060345.png',
    alt: 'Silver bangles stacked on wrist',
    href: '/brands',
  },
  {
    label: 'Necklaces',
    src: '/assets/images/image-1788603736143.png',
    alt: 'Gold pendant necklace on model',
    href: '/brands',
  },
  {
    label: 'New In',
    src: '/assets/images/image-1788603719870.png',
    alt: 'Stacked silver gemstone rings on hand',
    href: '/brands',
  },
  {
    label: 'On Model',
    src: '/assets/images/image-1788615174087.png',
    alt: 'Model wearing gold hoop earrings and white ruffled blouse',
    href: '/brands',
  },
];

// ─── Sub-components ──────────────────────────────────────────────────────────
function StarRating({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#5FA828">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

function SectionDivider() {
  return (
    <div className="flex items-center my-8">
      <div className="flex-1 h-px bg-[#E0DDD8]" />
      <div className="w-1 h-1 rounded-full bg-[#E0DDD8] mx-3" />
      <div className="flex-1 h-px bg-[#E0DDD8]" />
    </div>
  );
}

function Eyebrow({ children, light = false }: { children: React.ReactNode; light?: boolean }) {
  return (
    <span
      className="font-sans text-[0.65rem] font-semibold tracking-[0.2em] uppercase block mb-3"
      style={{ color: light ? 'rgba(255,255,255,0.5)' : '#5FA828' }}
    >
      {children}
    </span>
  );
}

function FAQItem({ faq }: { faq: FAQ }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item">
      <button className="faq-btn" onClick={() => setOpen(!open)}>
        <span className="font-display text-lg font-light pr-8">{faq.question}</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className={`flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-45' : ''}`}
          style={{ color: '#5FA828' }}
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-48 pb-6' : 'max-h-0'}`}
      >
        <p className="font-sans text-sm text-[#6B6B6B] leading-relaxed">{faq.answer}</p>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function HomePage() {
  const [activeSpotlightImg, setActiveSpotlightImg] = useState(0);

  const spotlightImages = [
    { src: '/assets/images/image-1788603736143.png', alt: 'Gold pendant necklace on model' },
    { src: '/assets/images/image-1788603702598.png', alt: 'Stacked silver gemstone rings close-up' },
    { src: '/assets/images/image-1788603104673.png', alt: 'Gold bangles and rings on model wrist' },
    { src: '/assets/images/image-1788603719870.png', alt: 'Stacked silver gemstone rings on hand' },
    { src: '/assets/images/image-1788615214440.png', alt: 'Model wearing gold hoop earrings, gold bangles and delicate necklace outdoors' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">

      {/* ── NAVIGATION (shared header: logo + nav links) ─────────────────── */}
      <Navigation />

      {/* ── CATEGORY THUMBNAILS ──────────────────────────────────────────── */}
      <div className="bg-white border-b border-[#E0DDD8]">
        <div className="flex items-start justify-start md:justify-center gap-4 md:gap-10 py-4 md:py-6 px-4 md:px-6 overflow-x-auto">
          {categories.map((cat) => (
            <Link
              key={cat.label}
              href={cat.href}
              className="flex flex-col items-center gap-2 group flex-shrink-0"
            >
              <div
                className="w-[70px] h-[70px] md:w-[110px] md:h-[110px] rounded-full overflow-hidden border-2 border-transparent group-hover:border-[#5FA828] transition-all duration-200"
                style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}
              >
                <img
                  src={cat.src}
                  alt={cat.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <span className="font-sans text-[0.58rem] md:text-[0.62rem] tracking-[0.12em] md:tracking-[0.14em] uppercase text-[#2B2B2B] group-hover:text-[#5FA828] transition-colors duration-200 text-center">
                {cat.label}
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* ── HERO BANNER ──────────────────────────────────────────────────── */}
      <div className="px-3 md:px-6 py-3 md:py-6 bg-[#FAFAFA]">
        <section
          className="relative flex items-end justify-between px-5 md:px-8 pb-10 md:pb-16 overflow-hidden rounded-lg mx-auto"
          style={{ minHeight: '60vh', background: '#1A1A1A', maxWidth: '1200px' }}
        >
        {/* Hero image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/images/8049eb7d-8628-46f6-ac86-c9d65d979fd5-1788625938644.png"
            alt="Blue door with silver jewellery display at Amanda Jones Jewellery shop"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.2) 100%)' }} />
        </div>

        {/* Hero content */}
        <div className="relative z-10 max-w-3xl w-full">
          {/* Location pill */}
          <div className="inline-flex items-center gap-2 border border-white/30 rounded-sm px-3 py-1.5 mb-5 md:mb-8">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span className="font-sans text-[0.6rem] tracking-[0.18em] uppercase text-white/80">Cowbridge, South Wales</span>
          </div>

          <h1
            className="font-display text-white mb-5 md:mb-6"
            style={{ fontSize: 'clamp(2.4rem, 8vw, 7rem)', fontWeight: 300, lineHeight: 0.92 }}
          >
            Contemporary
            <br />
            <span style={{ fontStyle: 'italic' }}>Silver Jewellery,</span>
            <br />
            Chosen With Care.
          </h1>

          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
            <Link href="/visit" className="btn-primary text-center">Visit Us in Cowbridge</Link>
            <Link href="/brands" className="btn-secondary-white text-center">Shop the Brands</Link>
            <div className="flex items-center gap-3 sm:ml-2">
              <a
                href="https://www.instagram.com/amanda13.jewellery/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/60 hover:text-white transition-colors"
                aria-label="Instagram"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" />
                </svg>
              </a>
            </div>
          </div>
        </div>

      </section>
      </div>

      {/* ── TRUST BAR ────────────────────────────────────────────────────── */}
      <section className="trust-bar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 md:justify-between md:gap-0">
            {[
              'Independent Boutique',
              'Cowbridge, South Wales',
              'Designer Stockist',
              '5.0 ★ Google Rating',
            ].map((item, i) => (
              <React.Fragment key={item}>
                <span className="font-sans text-[0.6rem] md:text-[0.65rem] tracking-[0.15em] uppercase text-white/70">{item}</span>
                {i < 3 && (
                  <span className="hidden md:block text-white/20 text-xs">&middot;</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT WE STOCK ────────────────────────────────────────────────── */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 md:mb-12">
            <div>
              <Eyebrow>What We Stock</Eyebrow>
              <h2
                className="font-display text-[#2B2B2B]"
                style={{ fontSize: 'clamp(2rem, 5vw, 4.5rem)', fontWeight: 300, lineHeight: 0.95 }}
              >
                Designer Brands,
                <br />
                <span style={{ fontStyle: 'italic' }}>Curated In-Store.</span>
              </h2>
            </div>
            <Link href="/brands" className="btn-primary mt-5 md:mt-0 self-start md:self-auto">
              See All Brands
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {/* Supporting image — hidden on mobile to save space */}
            <div className="hidden lg:block lg:col-span-1">
              <div className="rounded-[4px] w-full overflow-hidden" style={{ aspectRatio: '3/4', minHeight: '320px' }}>
                <img
                  src="/assets/images/image-1788615201339.png"
                  alt="Model wearing silver hoop earrings, silver cuff bracelet and ring with white shirt"
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="font-sans text-sm text-[#6B6B6B] mt-4 leading-relaxed">
                Hand-picked designer silver, curated in-store.
              </p>
            </div>

            {/* Brand tiles grid */}
            <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
              {brands.map((brand) => (
                <Link
                  key={brand.name}
                  href={brand.href}
                  className="brand-tile bg-white border border-[#E0DDD8] rounded-[4px] p-3 md:p-5 flex flex-col gap-1 md:gap-2 group"
                >
                  <span className="font-display text-base md:text-lg text-[#2B2B2B] group-hover:text-[#5FA828] transition-colors" style={{ fontWeight: 400 }}>
                    {brand.name}
                  </span>
                  <span className="font-sans text-[0.68rem] md:text-xs text-[#9B9690] leading-snug">{brand.tagline}</span>
                  <div className="mt-auto pt-2 md:pt-3">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5FA828" strokeWidth="1.5" className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* ── EARRINGS ON MODEL ────────────────────────────────────────────── */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-[#F5F3EF]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 md:mb-12">
            <div>
              <Eyebrow>On Model</Eyebrow>
              <h2
                className="font-display text-[#2B2B2B]"
                style={{ fontSize: 'clamp(2rem, 5vw, 4.5rem)', fontWeight: 300, lineHeight: 0.95 }}
              >
                Earrings,
                <br />
                <span style={{ fontStyle: 'italic' }}>Styled to Wear.</span>
              </h2>
            </div>
            <Link href="/brands" className="btn-primary mt-5 md:mt-0 self-start md:self-auto">
              Explore Earrings
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 md:gap-6">
            <div className="rounded-[4px] overflow-hidden" style={{ aspectRatio: '3/4' }}>
              <img
                src="/assets/images/image-1788615174087.png"
                alt="Model wearing gold hoop earrings and white ruffled blouse"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-[4px] overflow-hidden" style={{ aspectRatio: '3/4' }}>
              <img
                src="/assets/images/image-1788615187560.png"
                alt="Model in black dress wearing pearl and white earrings with pearl clutch bag"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* ── FEATURED BRAND SPOTLIGHT ─────────────────────────────────────── */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-[#2B2B2B]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 md:mb-12">
            <div>
              <Eyebrow light>Featured Brand</Eyebrow>
              <h2
                className="font-display text-white"
                style={{ fontSize: 'clamp(2rem, 5vw, 4.5rem)', fontWeight: 300, lineHeight: 0.95 }}
              >
                Uno de 50
                <br />
                <span style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.6)' }}>Restaurant Renovation</span>
              </h2>
            </div>
            <p className="font-sans text-sm text-white/50 max-w-sm mt-3 md:mt-0 leading-relaxed">
              Handcrafted in Madrid since 1996 — bold, sculptural silver jewellery with a story in every piece.
            </p>
          </div>

          {/* Image gallery */}
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2 md:gap-3 mb-8 md:mb-12">
            {spotlightImages.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveSpotlightImg(i)}
                className={`relative overflow-hidden rounded-[4px] transition-all duration-200 ${
                  activeSpotlightImg === i ? 'ring-2 ring-[#5FA828]' : 'opacity-70 hover:opacity-100'
                }`}
                style={{ aspectRatio: '1/1' }}
              >
                {'src' in img ? (
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover"
                    style={{ minHeight: '80px' }}
                  />
                ) : (
                  <div className="img-placeholder w-full h-full" style={{ minHeight: '80px', borderRadius: '4px' }}>
                    <span className="text-[0.6rem]">{img.label}</span>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1 md:p-2">
                  <span className="font-sans text-[0.5rem] md:text-[0.55rem] tracking-[0.1em] uppercase text-white/70">
                    {['Necklaces', 'Rings', 'Bracelets', 'On-Model', 'In-Store'][i]}
                  </span>
                </div>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {/* Brand story */}
            <div>
              <h3 className="font-display text-white text-xl md:text-2xl mb-4" style={{ fontWeight: 300 }}>Why We Stock It</h3>
              <div className="flex items-center my-4">
                <div className="flex-1 h-px bg-white/10" />
                <div className="w-1 h-1 rounded-full bg-white/10 mx-3" />
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <p className="font-sans text-sm text-white/60 leading-relaxed mb-4">
                Uno de 50 is one of the most distinctive jewellery brands in the world. Each piece is handcrafted in Madrid using traditional techniques, with only 50 units ever made of each design — hence the name.
              </p>
              <p className="font-sans text-sm text-white/60 leading-relaxed mb-6">
                We stock the full range of necklaces, bracelets, rings, and earrings. Price range: £40 – £350. Perfect for gifting or treating yourself to something truly unique.
              </p>
              <div className="flex items-center gap-3">
                <span className="font-sans text-[0.65rem] tracking-[0.15em] uppercase text-white/30">Price Range</span>
                <span className="font-sans text-sm text-[#5FA828] font-semibold">££</span>
              </div>
            </div>

            {/* Testimonial card */}
            <div className="bg-white/5 border border-white/10 rounded-[4px] p-6 md:p-8">
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#5FA828">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
              <p className="font-display text-white/80 text-base md:text-lg leading-relaxed mb-5 md:mb-6" style={{ fontWeight: 300, fontStyle: 'italic' }}>
                &ldquo;I bought my first Uno de 50 piece from Amanda&rsquo;s boutique and I&rsquo;ve been hooked ever since. The quality is extraordinary and Amanda always knows exactly which piece will suit you.&rdquo;
              </p>
              <div className="flex items-center w-full my-4">
                <div className="flex-1 h-px bg-white/10" />
                <div className="w-1 h-1 rounded-full bg-white/10 mx-3" />
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <p className="font-sans text-sm text-white font-semibold">Emma L.</p>
              <p className="font-sans text-xs text-white/40 mt-1">Cardiff — Google Review ★★★★★</p>
              <Link href="/visit" className="btn-primary mt-5 md:mt-6 inline-flex">
                Explore Uno de 50 In-Store
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── INSTAGRAM / NEW IN ───────────────────────────────────────────── */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-[#F5F3EF]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <Eyebrow>New In</Eyebrow>
            <h2
              className="font-display text-[#2B2B2B]"
              style={{ fontSize: 'clamp(2rem, 5vw, 4rem)', fontWeight: 300, lineHeight: 0.95 }}
            >
              Latest Arrivals
            </h2>
            <div className="flex items-center justify-center my-5 md:my-6">
              <div className="w-16 h-px bg-[#E0DDD8]" />
              <div className="w-1 h-1 rounded-full bg-[#E0DDD8] mx-3" />
              <div className="w-16 h-px bg-[#E0DDD8]" />
            </div>
            <p className="font-sans text-sm text-[#6B6B6B]">
              Follow{' '}
              <a
                href="https://www.instagram.com/amanda13.jewellery/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#5FA828] hover:underline"
              >
                @amanda13.jewellery
              </a>{' '}
              for new arrivals
            </p>
          </div>

          {/* Instagram embed placeholder */}
          <div className="bg-white border border-[#E0DDD8] rounded-[4px] p-5 md:p-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#5FA828" strokeWidth="1.5">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.5" cy="6.5" r="0.5" fill="#5FA828" />
              </svg>
              <span className="font-sans text-sm font-semibold text-[#2B2B2B]">@amanda13.jewellery</span>
            </div>
            <p className="font-sans text-sm text-[#9B9690] mb-5 md:mb-6">
              Instagram feed widget — add your Elfsight or SnapWidget embed code here.
            </p>
            {/* Placeholder grid */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="img-placeholder rounded-[4px]"
                  style={{ aspectRatio: '1/1', minHeight: '60px' }}
                >
                  <span className="text-[0.55rem]">[IG post {i + 1}]</span>
                </div>
              ))}
            </div>
            <a
              href="https://www.instagram.com/amanda13.jewellery/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary mt-5 md:mt-6 inline-flex"
            >
              View on Instagram
            </a>
          </div>
        </div>
      </section>

      {/* ── REVIEWS ──────────────────────────────────────────────────────── */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 md:mb-12">
            <div>
              <Eyebrow>What Clients Say</Eyebrow>
              <h2
                className="font-display text-[#2B2B2B]"
                style={{ fontSize: 'clamp(2rem, 5vw, 4rem)', fontWeight: 300, lineHeight: 0.95 }}
              >
                5.0 on Google
              </h2>
            </div>
            <div className="flex items-center gap-3 mt-4 md:mt-0">
              <div className="flex flex-col items-center bg-white border border-[#E0DDD8] rounded-[4px] px-5 md:px-6 py-3 md:py-4">
                <span className="font-display text-3xl md:text-4xl text-[#2B2B2B]" style={{ fontWeight: 300 }}>5.0</span>
                <StarRating count={5} />
                <span className="font-sans text-[0.6rem] tracking-[0.1em] uppercase text-[#9B9690] mt-1">Google Reviews</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-10">
            {reviews.map((review, i) => (
              <div key={i} className="review-card">
                <StarRating count={review.stars} />
                <p className="font-display text-[#2B2B2B] text-base leading-relaxed my-3 md:my-4" style={{ fontWeight: 300, fontStyle: 'italic' }}>
                  {review.quote}
                </p>
                <div className="flex items-center my-3">
                  <div className="flex-1 h-px bg-[#E0DDD8]" />
                  <div className="w-1 h-1 rounded-full bg-[#E0DDD8] mx-3" />
                  <div className="flex-1 h-px bg-[#E0DDD8]" />
                </div>
                <p className="font-sans text-sm font-semibold text-[#2B2B2B]">{review.author}</p>
                <p className="font-sans text-xs text-[#9B9690] mt-1">{review.context}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <a
              href="https://www.google.com/search?q=Amanda+Jones+Jewellery+Cowbridge"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              Read More Reviews on Google
            </a>
          </div>
        </div>
      </section>

      {/* ── HOW TO SHOP / THE PROCESS ────────────────────────────────────── */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-[#F5F3EF]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 md:mb-12">
            <Eyebrow>How It Works</Eyebrow>
            <h2
              className="font-display text-[#2B2B2B]"
              style={{ fontSize: 'clamp(2rem, 5vw, 4rem)', fontWeight: 300, lineHeight: 0.95 }}
            >
              How to Shop
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
            {processSteps.map((step) => (
              <div key={step.number} className="flex flex-col">
                <span className="process-number">{step.number}</span>
                <div className="flex items-center my-3 md:my-4">
                  <div className="flex-1 h-px bg-[#E0DDD8]" />
                  <div className="w-1 h-1 rounded-full bg-[#E0DDD8] mx-3" />
                  <div className="flex-1 h-px bg-[#E0DDD8]" />
                </div>
                <h3 className="font-display text-lg md:text-xl text-[#2B2B2B] mb-2 md:mb-3" style={{ fontWeight: 400 }}>{step.title}</h3>
                <p className="font-sans text-xs md:text-sm text-[#6B6B6B] leading-relaxed">{step.description}</p>
                {step.number === '02' && (
                  <div className="mt-3 md:mt-4 rounded-[4px] overflow-hidden" style={{ aspectRatio: '4/3' }}>
                    <img
                      src="/assets/images/image-1788625216505.png"
                      alt="Glass display cabinet close-up with jewellery on stands inside Amanda Jones Jewellery boutique"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQs ─────────────────────────────────────────────────────────── */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 md:mb-12">
            <div>
              <Eyebrow>Common Questions</Eyebrow>
              <h2
                className="font-display text-[#2B2B2B]"
                style={{ fontSize: 'clamp(2rem, 5vw, 4rem)', fontWeight: 300, lineHeight: 0.95 }}
              >
                FAQs
              </h2>
            </div>
            <Link href="/contact" className="btn-secondary mt-5 md:mt-0 self-start">
              Contact Us
            </Link>
          </div>

          <div className="max-w-3xl">
            {faqs.map((faq, i) => (
              <FAQItem key={i} faq={faq} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA BANNER ─────────────────────────────────────────────── */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-[#2B3B2E]">
        <div className="max-w-7xl mx-auto text-center">
          <Eyebrow light>Cowbridge, South Wales</Eyebrow>
          <h2
            className="font-display text-white mb-5 md:mb-6"
            style={{ fontSize: 'clamp(2rem, 5vw, 4.5rem)', fontWeight: 300, lineHeight: 0.95 }}
          >
            Come and See
            <br />
            <span style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.6)' }}>the Collection.</span>
          </h2>
          <div className="flex items-center justify-center my-6 md:my-8">
            <div className="w-16 h-px bg-white/20" />
            <div className="w-1 h-1 rounded-full bg-white/20 mx-3" />
            <div className="w-16 h-px bg-white/20" />
          </div>
          <p className="font-sans text-sm text-white/60 mb-2">Cowbridge High Street, South Wales</p>
          <p className="font-sans text-sm text-white/60 mb-7 md:mb-8">[Phone number TBC] &nbsp;&middot;&nbsp; Mon–Sat 9:30am–5:00pm</p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            <Link href="/visit" className="btn-primary text-center">Get Directions</Link>
            <a
              href="https://www.instagram.com/amanda13.jewellery/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary-white text-center"
            >
              Follow on Instagram
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
